import { baseApi as api } from "./baseApi";
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    getManifest: build.query<GetManifestApiResponse, GetManifestApiArg>({
      query: (queryArg) => ({
        url: `/v1/authoring/tenants/${queryArg.tenant}/manifest`,
      }),
    }),
    listTenants: build.query<ListTenantsApiResponse, ListTenantsApiArg>({
      query: () => ({ url: `/v1/authoring/tenants` }),
    }),
    listComponents: build.query<
      ListComponentsApiResponse,
      ListComponentsApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/authoring/tenants/${queryArg.tenant}/components`,
        params: {
          type: queryArg["type"],
          cursor: queryArg.cursor,
          limit: queryArg.limit,
        },
      }),
    }),
    createComponent: build.mutation<
      CreateComponentApiResponse,
      CreateComponentApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/authoring/tenants/${queryArg.tenant}/components`,
        method: "POST",
        body: queryArg.createComponent,
      }),
    }),
    getAuthoringComponent: build.query<
      GetAuthoringComponentApiResponse,
      GetAuthoringComponentApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/authoring/tenants/${queryArg.tenant}/components/${queryArg.id}`,
      }),
    }),
    getAuthoringComponentRevision: build.query<
      GetAuthoringComponentRevisionApiResponse,
      GetAuthoringComponentRevisionApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/authoring/tenants/${queryArg.tenant}/components/${queryArg.id}/revisions/${queryArg.revision}`,
      }),
    }),
    listUsages: build.query<ListUsagesApiResponse, ListUsagesApiArg>({
      query: (queryArg) => ({
        url: `/v1/authoring/tenants/${queryArg.tenant}/components/${queryArg.id}/usages`,
        params: {
          cursor: queryArg.cursor,
          limit: queryArg.limit,
        },
      }),
    }),
    applyCommand: build.mutation<ApplyCommandApiResponse, ApplyCommandApiArg>({
      query: (queryArg) => ({
        url: `/v1/authoring/tenants/${queryArg.tenant}/components/${queryArg.id}/commands`,
        method: "POST",
        body: queryArg.command,
        headers: {
          "If-Match": queryArg["If-Match"],
        },
      }),
    }),
    listTenantDomains: build.query<
      ListTenantDomainsApiResponse,
      ListTenantDomainsApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/authoring/tenants/${queryArg.tenant}/domains`,
      }),
    }),
    grantUpload: build.mutation<GrantUploadApiResponse, GrantUploadApiArg>({
      query: (queryArg) => ({
        url: `/v1/authoring/tenants/${queryArg.tenant}/components/${queryArg.id}/uploads`,
        method: "POST",
        body: queryArg.uploadRequest,
      }),
    }),
    finaliseUpload: build.mutation<
      FinaliseUploadApiResponse,
      FinaliseUploadApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/authoring/tenants/${queryArg.tenant}/components/${queryArg.id}/uploads/finalise`,
        method: "POST",
        body: queryArg.finaliseUpload,
      }),
    }),
    listPublications: build.query<
      ListPublicationsApiResponse,
      ListPublicationsApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/authoring/tenants/${queryArg.tenant}/websites/${queryArg.website}/publications`,
      }),
    }),
    publishWebsite: build.mutation<
      PublishWebsiteApiResponse,
      PublishWebsiteApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/authoring/tenants/${queryArg.tenant}/websites/${queryArg.website}/publications`,
        method: "POST",
        body: queryArg.publishWebsite,
      }),
    }),
    getPublication: build.query<
      GetPublicationApiResponse,
      GetPublicationApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/authoring/tenants/${queryArg.tenant}/websites/${queryArg.website}/publications/${queryArg.publication}`,
      }),
    }),
    promotePublication: build.mutation<
      PromotePublicationApiResponse,
      PromotePublicationApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/authoring/tenants/${queryArg.tenant}/websites/${queryArg.website}/publications/current`,
        method: "PUT",
        body: queryArg.promotePublication,
      }),
    }),
    getPublicationChanges: build.query<
      GetPublicationChangesApiResponse,
      GetPublicationChangesApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/authoring/tenants/${queryArg.tenant}/websites/${queryArg.website}/changes`,
        params: {
          path: queryArg.path,
        },
      }),
    }),
    getPublicationOperation: build.query<
      GetPublicationOperationApiResponse,
      GetPublicationOperationApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/authoring/tenants/${queryArg.tenant}/operations/${queryArg.operationId}`,
      }),
    }),
    listSubmissions: build.query<
      ListSubmissionsApiResponse,
      ListSubmissionsApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/authoring/tenants/${queryArg.tenant}/websites/${queryArg.website}/submissions`,
        params: {
          filtered: queryArg.filtered,
          form: queryArg.form,
          cursor: queryArg.cursor,
          limit: queryArg.limit,
        },
      }),
    }),
    getSubmission: build.query<GetSubmissionApiResponse, GetSubmissionApiArg>({
      query: (queryArg) => ({
        url: `/v1/authoring/tenants/${queryArg.tenant}/websites/${queryArg.website}/submissions/${queryArg.submission}`,
      }),
    }),
    judgeSubmission: build.mutation<
      JudgeSubmissionApiResponse,
      JudgeSubmissionApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/authoring/tenants/${queryArg.tenant}/websites/${queryArg.website}/submissions/${queryArg.submission}/judgement`,
        method: "POST",
        body: queryArg.judgeSubmission,
      }),
    }),
    resendSubmission: build.mutation<
      ResendSubmissionApiResponse,
      ResendSubmissionApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/authoring/tenants/${queryArg.tenant}/websites/${queryArg.website}/submissions/${queryArg.submission}/resend`,
        method: "POST",
      }),
    }),
    getWebsiteTraffic: build.query<
      GetWebsiteTrafficApiResponse,
      GetWebsiteTrafficApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/authoring/tenants/${queryArg.tenant}/websites/${queryArg.website}/traffic`,
        params: {
          from: queryArg["from"],
          to: queryArg.to,
        },
      }),
    }),
    checkRendererCoverage: build.mutation<
      CheckRendererCoverageApiResponse,
      CheckRendererCoverageApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/authoring/tenants/${queryArg.tenant}/renderer/coverage`,
        method: "POST",
        body: queryArg.rendererManifest,
      }),
    }),
    getRendererContract: build.query<
      GetRendererContractApiResponse,
      GetRendererContractApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/authoring/tenants/${queryArg.tenant}/renderer/contract`,
      }),
    }),
    listEventTypes: build.query<
      ListEventTypesApiResponse,
      ListEventTypesApiArg
    >({
      query: () => ({ url: `/v1/authoring/event-types` }),
    }),
    listWebhooks: build.query<ListWebhooksApiResponse, ListWebhooksApiArg>({
      query: (queryArg) => ({
        url: `/v1/authoring/tenants/${queryArg.tenant}/webhooks`,
      }),
    }),
    createWebhook: build.mutation<
      CreateWebhookApiResponse,
      CreateWebhookApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/authoring/tenants/${queryArg.tenant}/webhooks`,
        method: "POST",
        body: queryArg.createWebhook,
      }),
    }),
    getWebhook: build.query<GetWebhookApiResponse, GetWebhookApiArg>({
      query: (queryArg) => ({
        url: `/v1/authoring/tenants/${queryArg.tenant}/webhooks/${queryArg.webhook}`,
      }),
    }),
    updateWebhook: build.mutation<
      UpdateWebhookApiResponse,
      UpdateWebhookApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/authoring/tenants/${queryArg.tenant}/webhooks/${queryArg.webhook}`,
        method: "PATCH",
        body: queryArg.updateWebhook,
      }),
    }),
    deleteWebhook: build.mutation<
      DeleteWebhookApiResponse,
      DeleteWebhookApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/authoring/tenants/${queryArg.tenant}/webhooks/${queryArg.webhook}`,
        method: "DELETE",
      }),
    }),
    rotateWebhookSecret: build.mutation<
      RotateWebhookSecretApiResponse,
      RotateWebhookSecretApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/authoring/tenants/${queryArg.tenant}/webhooks/${queryArg.webhook}/secret`,
        method: "POST",
      }),
    }),
    testWebhook: build.mutation<TestWebhookApiResponse, TestWebhookApiArg>({
      query: (queryArg) => ({
        url: `/v1/authoring/tenants/${queryArg.tenant}/webhooks/${queryArg.webhook}/test`,
        method: "POST",
      }),
    }),
    listWebhookDeliveries: build.query<
      ListWebhookDeliveriesApiResponse,
      ListWebhookDeliveriesApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/authoring/tenants/${queryArg.tenant}/webhooks/${queryArg.webhook}/deliveries`,
      }),
    }),
    redeliverWebhook: build.mutation<
      RedeliverWebhookApiResponse,
      RedeliverWebhookApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/authoring/tenants/${queryArg.tenant}/webhooks/${queryArg.webhook}/deliveries/${queryArg.delivery}/redeliver`,
        method: "POST",
      }),
    }),
    getAuthoringOpenApiyaml: build.query<
      GetAuthoringOpenApiyamlApiResponse,
      GetAuthoringOpenApiyamlApiArg
    >({
      query: () => ({ url: `/v1/authoring/openapi.yaml` }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as authoringApi };
export type GetManifestApiResponse =
  /** status 200 The registry, projected. */ Manifest;
export type GetManifestApiArg = {
  /** The tenant being administered. Every operation below this segment
    is authorised by one membership check on it.

    A tenant the caller does not hold is a 403, whether or not it
    exists — the surface does not report the existence of tenants a
    caller has no standing in.
     */
  tenant: string;
};
export type ListTenantsApiResponse =
  /** status 200 The caller's memberships. */ Tenants;
export type ListTenantsApiArg = void;
export type ListComponentsApiResponse =
  /** status 200 One page of the tenant's components, ordered by type and then
by id.
 */ Components;
export type ListComponentsApiArg = {
  /** The tenant being administered. Every operation below this segment
    is authorised by one membership check on it.

    A tenant the caller does not hold is a 403, whether or not it
    exists — the surface does not report the existence of tenants a
    caller has no standing in.
     */
  tenant: string;
  /** Narrow the listing to one component type, named as the manifest
    names it. Absent lists every type. An unknown name is an empty
    listing rather than an error.
     */
  type?: string;
  /** Continue a listing by handing back the `cursor` the previous page
    returned.

    Opaque, and bound to the tenant it was issued for. A cursor
    replayed under a different tenant is a 400.
     */
  cursor?: string;
  /** Maximum rows per page. A short page does not mean the listing has
    ended — only an absent `cursor` does.
     */
  limit?: number;
};
export type CreateComponentApiResponse =
  /** status 201 The component at its first revision — the same document a
subsequent `GET` returns, so a client can render immediately
without a second call.
 */ Document;
export type CreateComponentApiArg = {
  /** The tenant being administered. Every operation below this segment
    is authorised by one membership check on it.

    A tenant the caller does not hold is a 403, whether or not it
    exists — the surface does not report the existence of tenants a
    caller has no standing in.
     */
  tenant: string;
  createComponent: CreateComponent;
};
export type GetAuthoringComponentApiResponse =
  /** status 200 The component. */ Document;
export type GetAuthoringComponentApiArg = {
  /** The tenant being administered. Every operation below this segment
    is authorised by one membership check on it.

    A tenant the caller does not hold is a 403, whether or not it
    exists — the surface does not report the existence of tenants a
    caller has no standing in.
     */
  tenant: string;
  /** The component's own identifier, without its tenant — the tenant is
    the preceding path segment.

    Assigned when the component is created and returned in that
    response. It is not chosen by the caller and cannot be predicted;
    read it from the create response or from a listing.
     */
  id: string;
};
export type GetAuthoringComponentRevisionApiResponse =
  /** status 200 The component at that revision. */ Document;
export type GetAuthoringComponentRevisionApiArg = {
  /** The tenant being administered. Every operation below this segment
    is authorised by one membership check on it.

    A tenant the caller does not hold is a 403, whether or not it
    exists — the surface does not report the existence of tenants a
    caller has no standing in.
     */
  tenant: string;
  /** The component's own identifier, without its tenant — the tenant is
    the preceding path segment.

    Assigned when the component is created and returned in that
    response. It is not chosen by the caller and cannot be predicted;
    read it from the create response or from a listing.
     */
  id: string;
  /** A revision number, counting from 1. Revisions are contiguous and
    every one of them exists.
     */
  revision: number;
};
export type ListUsagesApiResponse =
  /** status 200 One page of the components holding this one, ordered by id.
   */ Usages;
export type ListUsagesApiArg = {
  /** The tenant being administered. Every operation below this segment
    is authorised by one membership check on it.

    A tenant the caller does not hold is a 403, whether or not it
    exists — the surface does not report the existence of tenants a
    caller has no standing in.
     */
  tenant: string;
  /** The component's own identifier, without its tenant — the tenant is
    the preceding path segment.

    Assigned when the component is created and returned in that
    response. It is not chosen by the caller and cannot be predicted;
    read it from the create response or from a listing.
     */
  id: string;
  /** Continue a listing by handing back the `cursor` the previous page
    returned.

    Opaque, and bound to the tenant it was issued for. A cursor
    replayed under a different tenant is a 400.
     */
  cursor?: string;
  /** Maximum rows per page. A short page does not mean the listing has
    ended — only an absent `cursor` does.
     */
  limit?: number;
};
export type ApplyCommandApiResponse =
  /** status 200 The component after the intent was applied. */ Document;
export type ApplyCommandApiArg = {
  /** The tenant being administered. Every operation below this segment
    is authorised by one membership check on it.

    A tenant the caller does not hold is a 403, whether or not it
    exists — the surface does not report the existence of tenants a
    caller has no standing in.
     */
  tenant: string;
  /** The component's own identifier, without its tenant — the tenant is
    the preceding path segment.

    Assigned when the component is created and returned in that
    response. It is not chosen by the caller and cannot be predicted;
    read it from the create response or from a listing.
     */
  id: string;
  /** Apply the command only when this strong quoted revision is current. */
  "If-Match"?: string;
  command: Command;
};
export type ListTenantDomainsApiResponse =
  /** status 200 The tenant's web addresses, ordered by host. */ TenantDomains;
export type ListTenantDomainsApiArg = {
  /** The tenant being administered. Every operation below this segment
    is authorised by one membership check on it.

    A tenant the caller does not hold is a 403, whether or not it
    exists — the surface does not report the existence of tenants a
    caller has no standing in.
     */
  tenant: string;
};
export type GrantUploadApiResponse =
  /** status 200 Where to send the file, and what it will be called once it is
there. Nothing has been created, which is why this is a 200
rather than a 201.
 */ Upload;
export type GrantUploadApiArg = {
  /** The tenant being administered. Every operation below this segment
    is authorised by one membership check on it.

    A tenant the caller does not hold is a 403, whether or not it
    exists — the surface does not report the existence of tenants a
    caller has no standing in.
     */
  tenant: string;
  /** The component's own identifier, without its tenant — the tenant is
    the preceding path segment.

    Assigned when the component is created and returned in that
    response. It is not chosen by the caller and cannot be predicted;
    read it from the create response or from a listing.
     */
  id: string;
  uploadRequest: UploadRequest;
};
export type FinaliseUploadApiResponse =
  /** status 200 The component at the revision that records the file — the same
document `GET` returns.
 */ Document;
export type FinaliseUploadApiArg = {
  /** The tenant being administered. Every operation below this segment
    is authorised by one membership check on it.

    A tenant the caller does not hold is a 403, whether or not it
    exists — the surface does not report the existence of tenants a
    caller has no standing in.
     */
  tenant: string;
  /** The component's own identifier, without its tenant — the tenant is
    the preceding path segment.

    Assigned when the component is created and returned in that
    response. It is not chosen by the caller and cannot be predicted;
    read it from the create response or from a listing.
     */
  id: string;
  finaliseUpload: FinaliseUpload;
};
export type ListPublicationsApiResponse =
  /** status 200 The Website's publication history. */ Publications;
export type ListPublicationsApiArg = {
  /** The tenant being administered. Every operation below this segment
    is authorised by one membership check on it.

    A tenant the caller does not hold is a 403, whether or not it
    exists — the surface does not report the existence of tenants a
    caller has no standing in.
     */
  tenant: string;
  /** The Website component id, without its tenant. */
  website: string;
};
export type PublishWebsiteApiResponse =
  /** status 202 The accepted or previously recorded operation. */ PublicationOperation;
export type PublishWebsiteApiArg = {
  /** The tenant being administered. Every operation below this segment
    is authorised by one membership check on it.

    A tenant the caller does not hold is a 403, whether or not it
    exists — the surface does not report the existence of tenants a
    caller has no standing in.
     */
  tenant: string;
  /** The Website component id, without its tenant. */
  website: string;
  publishWebsite: PublishWebsite;
};
export type GetPublicationApiResponse =
  /** status 200 The publication and its selected component revisions. */ Publication;
export type GetPublicationApiArg = {
  /** The tenant being administered. Every operation below this segment
    is authorised by one membership check on it.

    A tenant the caller does not hold is a 403, whether or not it
    exists — the surface does not report the existence of tenants a
    caller has no standing in.
     */
  tenant: string;
  /** The Website component id, without its tenant. */
  website: string;
  /** The publication revision. */
  publication: number;
};
export type PromotePublicationApiResponse =
  /** status 200 The resulting current and head publications. */ PublicationState;
export type PromotePublicationApiArg = {
  /** The tenant being administered. Every operation below this segment
    is authorised by one membership check on it.

    A tenant the caller does not hold is a 403, whether or not it
    exists — the surface does not report the existence of tenants a
    caller has no standing in.
     */
  tenant: string;
  /** The Website component id, without its tenant. */
  website: string;
  promotePublication: PromotePublication;
};
export type GetPublicationChangesApiResponse =
  /** status 200 The differing component revisions. */ PublicationChanges;
export type GetPublicationChangesApiArg = {
  /** The tenant being administered. Every operation below this segment
    is authorised by one membership check on it.

    A tenant the caller does not hold is a 403, whether or not it
    exists — the surface does not report the existence of tenants a
    caller has no standing in.
     */
  tenant: string;
  /** The Website component id, without its tenant. */
  website: string;
  /** A routed path to compare. */
  path?: string;
};
export type GetPublicationOperationApiResponse =
  /** status 200 The operation's current status. */ PublicationOperation;
export type GetPublicationOperationApiArg = {
  /** The tenant being administered. Every operation below this segment
    is authorised by one membership check on it.

    A tenant the caller does not hold is a 403, whether or not it
    exists — the surface does not report the existence of tenants a
    caller has no standing in.
     */
  tenant: string;
  /** The id supplied when publication was requested. */
  operationId: string;
};
export type ListSubmissionsApiResponse =
  /** status 200 One page of submissions. */ Submissions;
export type ListSubmissionsApiArg = {
  /** The tenant being administered. Every operation below this segment
    is authorised by one membership check on it.

    A tenant the caller does not hold is a 403, whether or not it
    exists — the surface does not report the existence of tenants a
    caller has no standing in.
     */
  tenant: string;
  /** The Website component id, without its tenant. */
  website: string;
  /** List what a defence stopped instead of what was delivered.
   */
  filtered?: boolean;
  /** Narrow to one Form component, named by its id without its
    tenant. Absent lists every form on the site.
     */
  form?: string;
  /** Continue a listing by handing back the `cursor` the previous page
    returned.

    Opaque, and bound to the tenant it was issued for. A cursor
    replayed under a different tenant is a 400.
     */
  cursor?: string;
  /** Maximum rows per page. A short page does not mean the listing
    has ended — only an absent `cursor` does.
     */
  limit?: number;
};
export type GetSubmissionApiResponse =
  /** status 200 The submission and its evidence. */ Submission;
export type GetSubmissionApiArg = {
  /** The tenant being administered. Every operation below this segment
    is authorised by one membership check on it.

    A tenant the caller does not hold is a 403, whether or not it
    exists — the surface does not report the existence of tenants a
    caller has no standing in.
     */
  tenant: string;
  /** The Website component id, without its tenant. */
  website: string;
  /** The submission's identifier, as the listing reports it.
   */
  submission: string;
};
export type JudgeSubmissionApiResponse =
  /** status 200 The submission, carrying the judgement. */ Submission;
export type JudgeSubmissionApiArg = {
  /** The tenant being administered. Every operation below this segment
    is authorised by one membership check on it.

    A tenant the caller does not hold is a 403, whether or not it
    exists — the surface does not report the existence of tenants a
    caller has no standing in.
     */
  tenant: string;
  /** The Website component id, without its tenant. */
  website: string;
  /** The submission's identifier, as the listing reports it.
   */
  submission: string;
  judgeSubmission: JudgeSubmission;
};
export type ResendSubmissionApiResponse =
  /** status 202 The submission, waiting to be delivered. */ Submission;
export type ResendSubmissionApiArg = {
  /** The tenant being administered. Every operation below this segment
    is authorised by one membership check on it.

    A tenant the caller does not hold is a 403, whether or not it
    exists — the surface does not report the existence of tenants a
    caller has no standing in.
     */
  tenant: string;
  /** The Website component id, without its tenant. */
  website: string;
  /** The submission's identifier, as the listing reports it.
   */
  submission: string;
};
export type GetWebsiteTrafficApiResponse =
  /** status 200 The site's traffic over the range. */ TrafficReport;
export type GetWebsiteTrafficApiArg = {
  /** The tenant being administered. Every operation below this segment
    is authorised by one membership check on it.

    A tenant the caller does not hold is a 403, whether or not it
    exists — the surface does not report the existence of tenants a
    caller has no standing in.
     */
  tenant: string;
  /** The Website component id, without its tenant. */
  website: string;
  /** The first day, inclusive. Absent is the Monday twelve complete
    Monday-to-Sunday weeks before the week `to` falls in.
     */
  from?: string;
  /** The last day, inclusive. Absent is the report's `today`. A
    range covers at most 400 days.
     */
  to?: string;
};
export type CheckRendererCoverageApiResponse =
  /** status 200 The coverage report. Gaps are `findings`, not an error status —
an uncovered release is a valid answer to a valid question.
 */ RendererCoverage;
export type CheckRendererCoverageApiArg = {
  /** The tenant being administered. Every operation below this segment
    is authorised by one membership check on it.

    A tenant the caller does not hold is a 403, whether or not it
    exists — the surface does not report the existence of tenants a
    caller has no standing in.
     */
  tenant: string;
  rendererManifest: RendererManifest;
};
export type GetRendererContractApiResponse =
  /** status 200 The contract, as a manifest with no renderer behind it:
`presentation` and `release` are empty.
 */ RendererManifest;
export type GetRendererContractApiArg = {
  /** The tenant being administered. Every operation below this segment
    is authorised by one membership check on it.

    A tenant the caller does not hold is a 403, whether or not it
    exists — the surface does not report the existence of tenants a
    caller has no standing in.
     */
  tenant: string;
};
export type ListEventTypesApiResponse =
  /** status 200 The public event catalogue. */ EventTypes;
export type ListEventTypesApiArg = void;
export type ListWebhooksApiResponse =
  /** status 200 The tenant's webhook subscriptions. */ Webhooks;
export type ListWebhooksApiArg = {
  /** The tenant being administered. Every operation below this segment
    is authorised by one membership check on it.

    A tenant the caller does not hold is a 403, whether or not it
    exists — the surface does not report the existence of tenants a
    caller has no standing in.
     */
  tenant: string;
};
export type CreateWebhookApiResponse =
  /** status 201 The webhook and its signing secret. The secret is not returned by reads. */ CreatedWebhook;
export type CreateWebhookApiArg = {
  /** The tenant being administered. Every operation below this segment
    is authorised by one membership check on it.

    A tenant the caller does not hold is a 403, whether or not it
    exists — the surface does not report the existence of tenants a
    caller has no standing in.
     */
  tenant: string;
  createWebhook: CreateWebhook;
};
export type GetWebhookApiResponse =
  /** status 200 The webhook without its signing secret. */ Webhook;
export type GetWebhookApiArg = {
  /** The tenant being administered. Every operation below this segment
    is authorised by one membership check on it.

    A tenant the caller does not hold is a 403, whether or not it
    exists — the surface does not report the existence of tenants a
    caller has no standing in.
     */
  tenant: string;
  /** The webhook subscription id. */
  webhook: string;
};
export type UpdateWebhookApiResponse =
  /** status 200 The updated webhook. */ Webhook;
export type UpdateWebhookApiArg = {
  /** The tenant being administered. Every operation below this segment
    is authorised by one membership check on it.

    A tenant the caller does not hold is a 403, whether or not it
    exists — the surface does not report the existence of tenants a
    caller has no standing in.
     */
  tenant: string;
  /** The webhook subscription id. */
  webhook: string;
  updateWebhook: UpdateWebhook;
};
export type DeleteWebhookApiResponse = unknown;
export type DeleteWebhookApiArg = {
  /** The tenant being administered. Every operation below this segment
    is authorised by one membership check on it.

    A tenant the caller does not hold is a 403, whether or not it
    exists — the surface does not report the existence of tenants a
    caller has no standing in.
     */
  tenant: string;
  /** The webhook subscription id. */
  webhook: string;
};
export type RotateWebhookSecretApiResponse =
  /** status 200 The new signing secret, shown only in this response. */ RotatedWebhookSecret;
export type RotateWebhookSecretApiArg = {
  /** The tenant being administered. Every operation below this segment
    is authorised by one membership check on it.

    A tenant the caller does not hold is a 403, whether or not it
    exists — the surface does not report the existence of tenants a
    caller has no standing in.
     */
  tenant: string;
  /** The webhook subscription id. */
  webhook: string;
};
export type TestWebhookApiResponse =
  /** status 202 The test delivery was queued. */ WebhookDelivery;
export type TestWebhookApiArg = {
  /** The tenant being administered. Every operation below this segment
    is authorised by one membership check on it.

    A tenant the caller does not hold is a 403, whether or not it
    exists — the surface does not report the existence of tenants a
    caller has no standing in.
     */
  tenant: string;
  /** The webhook subscription id. */
  webhook: string;
};
export type ListWebhookDeliveriesApiResponse =
  /** status 200 Recent delivery outcomes. */ WebhookDeliveries;
export type ListWebhookDeliveriesApiArg = {
  /** The tenant being administered. Every operation below this segment
    is authorised by one membership check on it.

    A tenant the caller does not hold is a 403, whether or not it
    exists — the surface does not report the existence of tenants a
    caller has no standing in.
     */
  tenant: string;
  /** The webhook subscription id. */
  webhook: string;
};
export type RedeliverWebhookApiResponse =
  /** status 202 The new delivery was queued. */ WebhookDelivery;
export type RedeliverWebhookApiArg = {
  /** The tenant being administered. Every operation below this segment
    is authorised by one membership check on it.

    A tenant the caller does not hold is a 403, whether or not it
    exists — the surface does not report the existence of tenants a
    caller has no standing in.
     */
  tenant: string;
  /** The webhook subscription id. */
  webhook: string;
  /** A webhook delivery id. */
  delivery: string;
};
export type GetAuthoringOpenApiyamlApiResponse =
  /** status 200 This document. */ string;
export type GetAuthoringOpenApiyamlApiArg = void;
export type ManifestProse = {
  /** The display name for one of them. */
  title: string;
  /** The display name for more than one. Absent where the type
    declares none, in which case use `title`.
     */
  plural?: string;
  /** One line saying what the type is for, for a control that
    offers a choice between types.
     */
  description?: string;
};
export type Schema = {
  [key: string]: any;
};
export type Condition = {
  /** The member holding the shape `field` is read in. Absent means
    `field` is a member of the shape itself.
     */
  in?: string;
  /** The member that decides. */
  field: string;
  /** The values it must hold. */
  is: string[];
};
export type ManifestIntent = {
  /** The permanent wire name, sent as a command's `intent`. */
  name: string;
  /** The words a control offering this intent uses — an
    imperative, in the type's own vocabulary.

    **Absent for an intent whose words have not been written**,
    in which case a client labels the control from `name`.
     */
  title?: string;
  /** What `Command.payload` may carry for this intent.

    **The payload only** — never the envelope. `path` and `actor`
    are absent from every one of these: a command states its own
    path, the surface states the actor, and a payload naming either
    is refused.
     */
  payload: Schema;
  /** When this intent applies, read against the `fields` of the node
    a command addresses. A command whose node fails it is refused,
    so offer the intent only where it holds. Absent means every
    node of the type may be sent it.
     */
  when?: Condition;
};
export type ManifestBinding = {
  /** The element that carries the reference, lowercased. */
  element: string;
  /** The attribute that holds it — the one that already holds an
    address, so the element says which binding is meant and
    nothing is carried twice.
     */
  ref: string;
  /** The type the named child must be. */
  accepts: string;
};
export type ManifestReference = {
  /** The role a target plays under this edge. It is what a
    reference names, and what a client labels the picker with.
     */
  name: string;
  /** The types a target may be. **Resolved, not declared**, exactly
    as `contains` is: an edge may accept by role, and every type in
    this tenant whose role it accepts is listed here by name, so a
    picker is this list and nothing else.
     */
  accepts: string[];
  /** The kinds of type this edge accepts, as declared. It says *why*
    a type is in `accepts`, which is what lets a client explain
    that an edge takes any record rather than these particular
    seven types.
     */
  acceptsRoles?: ("site" | "page" | "layout" | "record" | "block")[];
  /** Whether several references may share this name. False means
    cardinality one: a second is refused rather than resolved by
    whichever the walk reached first.
     */
  multiple: boolean;
  /** When true, `name` labels a **family** rather than an edge: the
    references in it are named by whoever writes them, and what
    each name means comes from elsewhere. A pattern placement
    carries one such edge per record its declaration reads, under
    the names that declaration gives. `accepts` and `multiple`
    still apply to every edge in the family. Absent means the
    ordinary case, where `name` is the edge.
     */
  authoredNames?: boolean;
  /** When true, the target belongs to the record. Creating a record
    without this edge creates its target too, and the record
    refers to it. A client edits the target in place rather than
    offering a picker. Absent means the target is chosen.
     */
  owned?: boolean;
  /** When a holder may hold this edge, read against its own
    `fields`. A reference on a holder that fails it is refused, so
    offer the edge only where it holds. Absent means every holder
    of the type may hold it.
     */
  when?: Condition;
};
export type ManifestType = {
  /** The type's permanent name. */
  name: string;
  /** What this type is called, in words, as the type itself
    declares them.

    **Absent for a type whose prose has not been written.** A
    client with a name to show falls back to whatever it can make
    of `name`.
     */
  prose?: ManifestProse;
  /** What a document of this type **holds** — the schema of
    `Document.fields` for a component of this type.

    **It is not what a client sends.** There is no generic
    field-set intent. Use this to display a document and prefill a
    control; `intents[].payload` is the only thing that changes
    anything.
     */
  fields: Schema;
  /** The intents this type declares — what a command may name in
    `intent` against a component of this type, and what it may send
    with each.
     */
  intents: ManifestIntent[];
  /** The types this one may hold as children, enforced against
    linked and inline children alike. A client's palette for a node
    is this list.

    **Resolved, not declared.** A type may admit children by *role*
    as well as by name, and this carries the result: every type in
    this tenant whose role the holder accepts is listed here by
    name, so a palette is this list and nothing else.
     */
  contains: string[];
  /** Whether an instance may be defined in place inside another
    component. A type that carries identity — Media, whose
    component id is the library identity of an uploaded file — is
    always standalone and linked.
     */
  inlineable: boolean;
  /** Whether a component of this type holds a stored file, and may
    therefore be the target of `POST …/components/{id}/uploads`.

    The intent that records a landed file is deliberately absent
    from `intents`: it is written by the finalise operation and can
    never be commanded.
     */
  acceptsUpload: boolean;
  /** When a component of this type holds a file, where only some of
    them do. Read it against the component's own `fields`: a
    document that fails it is refused a grant, so offer no
    uploader for one.

    Absent where `acceptsUpload` is true for every component of
    the type, and absent whenever it is false.
     */
  uploadWhen?: Condition;
  /** What kind of thing this type is, in the platform's own
    vocabulary: `site`, `page`, `layout`, `record` or `block`.

    It is what a role-based containment resolves against, and it is
    how a client tells a record from a block without knowing either
    by name. **Absent for a type that declares none** — a poorer
    palette rather than a broken one.
     */
  role?: "site" | "page" | "layout" | "record" | "block";
  /** The fields that name a component of this type, tried in order;
    the first holding any text is its `label`. Each is a string
    member of `fields`. Absent for a type that names none, whose
    components are listed under their ids.
     */
  label?: string[];
  /** The field whose value addresses a record's generated page. Two
    records a route serves that share a value, or a record with none,
    have no page. **Absent for a type that declares no key.**
     */
  key?: string;
  /** The Collection a new record of this type joins when it is
    created, at the end of its order. Absent for a type whose
    records join Collections only when placed there.
     */
  collection?: string;
  /** The kinds of type this one may hold, as declared rather than
    resolved. `contains` already lists every type the role admits,
    so a palette needs only that; this says *why* a type is in it,
    which is what lets a client explain that a slot takes any
    record rather than these particular seven types.
     */
  containsRoles?: ("site" | "page" | "layout" | "record" | "block")[];
  /** Whether documents of this type **are component type
    definitions** — the meta-type, of which a tenant creates
    instances to declare types of its own.

    It is stated so a client can offer a type designer by asking
    rather than by knowing which type is the meta-type, on the
    same terms `acceptsUpload` answers for the type that holds a
    file. Exactly one type declares it, and a build shipping none
    answers false everywhere.
     */
  defines: boolean;
  /** Whether documents of this type **are pattern declarations** —
    what a pattern accepts as input, the properties a placement
    supplies, and the tree it emits.

    A placement's `props` are typed by the declaration it refers
    to rather than by its own type, so a client draws that form
    from the declaration's `derived.propsSchema` and finds the
    declaration among the placement's references by this. Exactly
    one type declares it, and a build shipping none answers false
    everywhere.
     */
  declaresPatterns: boolean;
  /** The named regions this type arranges children into, in the
    order a client should offer them — declaration order, not
    sorted.

    **Absent for a type that arranges no regions**, which is most
    of them. Absent is not an empty list: it says regions are not
    this type's concept.

    **Advisory.** A slot name is still whatever string an intent
    used, and a component holding an undeclared one stays valid.
     */
  slots?: string[];
  /** Fields an instance **states when it is created and can never
    change**.

    It is the other half of a withheld intent. A create form is
    derived from a type's fields and offers a property when the
    type cannot do without it, or when some intent could set it
    anyway; withholding the only intent that named a field would
    otherwise take that field away from creation too, leaving one
    nothing can set. A Content's `format` is the case: the choice
    is the author's, and only the *changing* of it is not.

    A client offers these on the create form and nowhere else. It
    is not a second refusal — the surface already refuses the
    intent — it is what tells a form which moment to ask in.

    **Absent for a type with no such field**, which is most of
    them.
     */
  fixed?: string[];
  /** What this type's **rich text may name**, and where in the
    markup it names it.

    A body arranges its children the way every other type's fields
    do — a Page maps slot names to link ids, and a body says where
    in a sentence the thing goes. The spelling is a reserved URI
    in the attribute that already holds an address:
    `<a href="cms:c7">our web design work</a>`, where `c7` is a
    link id in this component's own `children`. A binding to a
    linked child restates the target after an `@`, as a guard
    against a paste that carried the markup and not the child.

    **This is what an editor draws from.** Each entry says the
    element, the attribute that holds the reference, and the type
    the named child must be, so a toolbar finds its work with a
    selector rather than with a list of its own.

    Delivery serves the body **as authored**: the reference stays
    in the markup and the child it names stays in `children`, and
    the consumer substitutes one for the other.

    **Absent for a type whose fields hold no such prose**, which
    is all but one of them, and absent is not an empty list.
     */
  bindings?: ManifestBinding[];
  /** The reference edges this type declares, sorted by name. A
    reference is a component this one *uses* rather than renders
    inside itself, and a name absent from this list is refused.

    **Absent for a type that declares none**, which is most of
    them, and absent is not an empty list — it says associations
    are not this type's concept.
     */
  references?: ManifestReference[];
};
export type Manifest = {
  /** The component types this build registered. */
  types: ManifestType[];
  /** The structural intents every component answers to, whatever its
    type — pinning a placement, promoting an inline child, deleting,
    reverting. They belong to no type, so a client reading only the
    per-type lists could not offer them.

    This is what may be **commanded**, which is narrower than what
    the platform can decode.
     */
  coreIntents: ManifestIntent[];
};
export type Problem = {
  /** A URI reference identifying the problem type. */
  type: string;
  /** A short, human-readable summary of the problem type. */
  title: string;
  /** The HTTP status code. */
  status: number;
  /** A human-readable explanation specific to this occurrence. */
  detail?: string;
  /** A URI reference identifying this occurrence. */
  instance?: string;
};
export type Membership = {
  /** The tenant's id, which is also its display name until tenant records exist. */
  tenant: string;
  /** What the caller may do within this tenant. Single-valued in this
    build.
     */
  role: "admin";
};
export type Tenants = {
  tenants: Membership[];
};
export type ComponentSummary = {
  /** The component's own identifier, without its tenant — a listing
    cannot cross one.
     */
  id: string;
  type: string;
  /** What this component calls itself, read from the type's own
    fields. **May be absent**; render the id when it is.

    Derived rather than authored — there is no rename operation for
    it, and editing whatever the type reads it from changes it at
    the next revision.
     */
  label?: string;
  /** The revision this row was derived from. It lags, so a client
    that pins or reverts uses the revision the document carries.
     */
  revision: number;
};
export type Components = {
  /** The page's rows, ordered by type and then by id. Always
    present, empty or not.
     */
  components: ComponentSummary[];
  /** Hand this back as `cursor` to continue. **Absent means the
    listing is exhausted** — a short page does not.
     */
  cursor?: string;
};
export type Reference = {
  /** The reference's identifier on this node, stable across
    revisions of the holder. It is a separate namespace from
    `linkId`: a node may hold `hero` as a child and `hero` as a
    reference, and neither means the other.
     */
  edgeId: string;
  /** The role the target plays for this holder. The manifest lists
    the names each type declares, and a name a type does not
    declare is refused.
     */
  name: string;
  /** The target, bare — it is in this document's tenant. */
  id: string;
  type: string;
  /** The revision the reference is pinned to. Absent means it
    resolves to the target's latest.
     */
  revision?: number;
};
export type Child = {
  /** The child's placement identifier, stable across revisions of the
    host. Placements are addressed by link id, never by component
    id: the same component linked twice is two placements.
     */
  linkId: string;
  type: string;
  /** A linked child's target, bare — it is in this document's tenant.
   */
  id?: string;
  /** The revision a linked child is pinned to. Absent means the
    placement resolves to the target's latest when composed.
     */
  revision?: number;
  /** An inline child's own fields. */
  fields?: {
    [key: string]: any;
  };
  /** An inline child's own children. */
  children?: Child[];
  /** An inline child's own references. */
  references?: Reference[];
};
export type Document = {
  /** Stated once, here. Ids below are bare, and no document may span
    tenants.
     */
  tenant: string;
  /** The component's own identifier, without its tenant. */
  id: string;
  type: string;
  /** What this component calls itself, read from the type's own
    fields. **May be absent**; render the id when it is.

    The same name a listing carries, read from the same
    declaration. Derived rather than authored: there is no rename
    operation for it, and editing whatever the type reads it from
    changes it at the next revision.
     */
  label?: string;
  /** The revision this document is. Also the caller's
    optimistic-concurrency token and the coordinate a pin uses.
     */
  revision: number;
  /** The type's own fields. Opaque to the core; the manifest carries
    their schema, under `types[].fields`.
     */
  fields?: {
    [key: string]: any;
  };
  /** What the component's **type** computes from its own fields and
    the deployment: a Media's `url`, and the url, byte size and
    checksum of each rendition made from its blob — the stored
    half of both is withheld, so this is where a client reads
    them. On a ComponentPattern it carries a `propsSchema`,
    which is the `Schema` for the `props` a placement of that
    declaration supplies.

    Read-only: no intent sets a derived field. It is separate from
    `fields` so a form prefilled from `fields` cannot pick one up
    and restate it as a command.

    **Absent when the type derives nothing**, and member by member
    when a derivation has nothing to say. Root document only.
     */
  derived?: {
    [key: string]: any;
  };
  /** The node's children, in declaration order. Always present,
    empty or not.
     */
  children: Child[];
  /** The node's references, in the order they were added. Absent
    when it holds none.
     */
  references?: Reference[];
  /** Whether the component is deleted. Deletion is a revision like
    any other, so a tombstoned component still reads.
     */
  tombstoned?: boolean;
  createdAt?: string;
  /** The subject of the identity that created it. */
  createdBy?: string;
  updatedAt?: string;
  /** The subject of the identity that last changed it. */
  updatedBy?: string;
};
export type ReferenceInput = {
  /** The reference's identifier on this node, unique among the
    node's other references.
     */
  edgeId: string;
  /** The role the target plays. It must be one the holder's type
    declares, and a name declared as a single reference may be used
    once.
     */
  name: string;
  /** The target, bare — resolved in this request's tenant.

    Bare **where this schema is referenced by name**, which is
    `references` on create. A reference reaching the surface inside
    a command's untyped `payload` carries `tenant#value` instead;
    see `Command.payload`.
     */
  id: string;
  /** The revision to pin the reference to. Absent leaves it
    resolving to the target's latest.
     */
  revision?: number;
};
export type ChildInput = {
  /** The child's placement identifier, stable across revisions of the
    host and unique among its siblings.
     */
  linkId: string;
  /** **Inline children only, and required for them.** Refused on a
    linked child, whose type is the target's.
     */
  type?: string;
  /** A linked child's target, bare — resolved in this request's
    tenant.

    Bare **where this schema is referenced by name**, which is
    `children` on create. A child reaching the surface inside a
    command's untyped `payload` carries `tenant#value` instead; see
    `Command.payload`.
     */
  id?: string;
  /** The revision to pin a linked child to. Absent leaves the
    placement resolving to the target's latest.
     */
  revision?: number;
  /** An inline child's own fields. */
  fields?: {
    [key: string]: any;
  };
  /** An inline child's own children. */
  children?: ChildInput[];
  /** An inline child's own references. */
  references?: ReferenceInput[];
};
export type CreateComponent = {
  /** A type name the manifest lists. */
  type: string;
  /** The type's own fields at the first revision. */
  fields?: {
    [key: string]: any;
  };
  /** Children to create the component holding. */
  children?: ChildInput[];
  /** References to create the component holding. A placement whose
    reference is what makes it meaningful is created with it rather
    than in a second revision.
     */
  references?: ReferenceInput[];
};
export type Usage = {
  /** The holding component's own identifier, without its tenant. A
    holder is always in its target's tenant.
     */
  id: string;
  type: string;
  /** What the holder calls itself, read from its type's own fields.
   **May be absent**; render the id when it is.
   */
  label?: string;
  /** The holder's revision this edge was derived from. It lags, so
    it is a hint for a row rather than a value to command against.
     */
  revision: number;
  /** How many placements in this holder name the target. The same
    component linked twice is two placements and one edge.
     */
  placements: number;
  /** How many of those placements are fixed to a revision.

    **This is what a delete dialog is asking.** A pinned placement
    resolves to the revision it names forever, so deleting the
    target does not disturb it. An unpinned one composes away.
     */
  pinned: number;
};
export type Usages = {
  /** The page's rows, ordered by the holder's id. Always present,
    empty or not.
     */
  usages: Usage[];
  /** Hand this back as `cursor` to continue. **Absent means the
    lookup is exhausted** — a short page does not. It is bound to
    the component it was issued for.
     */
  cursor?: string;
};
export type Command = {
  /** The intent's permanent wire name — one the manifest lists,
    either on the addressed node's type or among the core's
    structural intents.

    The manifest lists what may be commanded, which is narrower
    than what the platform can decode. Creating is `POST
    …/components`, not an intent here.
     */
  intent: string;
  /** The link path from the document root to the node this intent
    addresses. Absent or empty addresses the component's own
    document.

    A path may only descend through **inline** children: a linked
    child's content lives in its own stream, so editing it is a
    command against that component.
     */
  path?: string[];
  /** The intent's own members. **Described by the manifest**, under
    `intents[].payload`, rather than here — so registering a
    component type does not change this contract. Decoded strictly:
    an unknown member is a 400.

    **A linked child's `id` is composite inside a payload** —
    `tenant#value`, not the bare id used everywhere else, because
    the payload is untyped and nothing can tell which of its
    members are ids. Placing a page is
    `{"child": {"linkId": "c1", "id": "t-bluu#about"}}`, and the
    response renders that same child as `"id": "about"`.

    A child in a payload otherwise follows `ChildInput`: a linked
    child must not state a type, an inline child must.

    A `component.reverted` payload is `{"toRevision": n}` and
    nothing else — one stating `fields` or `children` is refused.
     */
  payload?: {
    [key: string]: any;
  };
};
export type TenantDomain = {
  /** Lowercase, with no scheme, port or path. */
  host: string;
  /** The platform operator granted this host to this tenant. */
  granted: boolean;
  /** The site of this tenant the host answers for, without its tenant.
    Absent when it answers for none of them.
     */
  website?: string;
  /** The host is the main address of `website`. */
  canonical?: boolean;
  /** The host answers for a site outside this tenant. */
  heldElsewhere: boolean;
  /** Sites of this tenant, without their tenant, whose `domains` list
    this host while it does not answer for them.
     */
  statedBy: string[];
};
export type TenantDomains = {
  /** Ordered by host. Always present, empty or not. */
  domains: TenantDomain[];
};
export type Upload = {
  /** `PUT` the file's bytes here, with no `Authorization` header —
    the signature is in the URL, and adding a credential would
    invalidate it.

    Send the browser's own `Content-Type` or none. It is not
    signed, so neither is refused.
     */
  url: string;
  /** Where the object will be once it is uploaded, and what finalise
    is called with. Opaque: a client that constructed one instead
    of using this would be refused.
     */
  key: string;
  /** When the signature stops being accepted. Expiry is not
    recoverable — ask for another grant, which is cheap.
     */
  expiresAt: string;
};
export type UploadRequest = {
  /** The file's name on the author's machine, carried in the issued
    key.

    It is **sanitised**, not rejected: directories are dropped and
    the alphabet is closed, so `../My Photo (1).JPG` becomes
    `My-Photo-1-.JPG`. The returned `key` is what it became.
     */
  filename: string;
};
export type FinaliseUpload = {
  /** The `key` the grant returned, after the `PUT` succeeded. It must
    be one this component was granted; anything else is a 400.
     */
  key: string;
};
export type PublicationSummary = {
  revision: number;
  publishedAt: string;
  current: boolean;
};
export type Publications = {
  current: number;
  head: number;
  publications: PublicationSummary[];
};
export type PublicationOperation = {
  id: string;
  website: string;
  kind: "publish" | "promote";
  status: "accepted" | "capturing" | "published" | "failed";
  publication?: number;
  error?: string;
  acceptedAt: string;
  updatedAt: string;
};
export type PublishWebsite = {
  /** The idempotency key retained while this operation is available. */
  operationId: string;
};
export type PublicationResolution = {
  id: string;
  revision: number;
};
export type PublicationSelection = {
  website: PublicationResolution;
  followed: PublicationResolution[];
  pinned: PublicationResolution[];
};
export type Publication = {
  revision: number;
  publishedAt: string;
  current: boolean;
  selection: PublicationSelection;
};
export type PublicationState = {
  current: number;
  head: number;
};
export type PromotePublication = {
  publication: number;
};
export type PublicationChange = {
  id: string;
  currentRevision: number;
  publishedRevision?: number;
};
export type PublicationChanges = {
  changed: boolean;
  changes: PublicationChange[];
};
export type SubmissionStatus =
  "accepted" | "sent" | "failed" | "spam" | "trapped";
export type SubmissionJudgement = {
  /** `ham` is a real enquiry; `spam` is not. */
  verdict: "ham" | "spam";
  by: string;
  at: string;
};
export type SubmissionSummary = {
  id: string;
  /** The Form component this answers, without its tenant. */
  form: string;
  /** The Form revision the visitor answered. Read the form at this
    revision to label the answers: a form edited since would
    describe them wrongly.
     */
  revision: number;
  /** The release that served the form, absent for older submissions. */
  publication?: number;
  status: SubmissionStatus;
  /** What the visitor answered, keyed by field name. Empty when the
    answers were discarded.
     */
  values: {
    [key: string]: any;
  };
  judgement?: SubmissionJudgement;
  acceptedAt: string;
};
export type Submissions = {
  submissions: SubmissionSummary[];
  /** Hand this back as `cursor` for the next page. Absent when the
    listing has ended.
     */
  cursor?: string;
};
export type SubmissionObserved = {
  /** The address the submission was received from. */
  ip?: string;
  userAgent?: string;
  /** When the platform accepted the submission. */
  at: string;
};
export type SubmissionReported = {
  ip?: string;
  userAgent?: string;
  referrer?: string;
  /** Seconds between the form rendering and the visitor submitting it. */
  elapsed?: number;
};
export type Submission = {
  id: string;
  /** The Form component this answers, without its tenant. */
  form: string;
  /** The Form revision the visitor answered. Read the form at this
    revision to label the answers.
     */
  revision: number;
  /** The release that served the form, absent for older submissions. */
  publication?: number;
  status: SubmissionStatus;
  /** What the visitor answered, keyed by field name. Empty when the
    answers were discarded.
     */
  values: {
    [key: string]: any;
  };
  judgement?: SubmissionJudgement;
  acceptedAt: string;
  observed: SubmissionObserved;
  reported?: SubmissionReported;
  /** When this submission is deleted. After it, the row is gone and
    the email is the only copy.
     */
  expiresAt?: string;
};
export type JudgeSubmission = {
  /** `ham` is a real enquiry; `spam` is not. */
  verdict: "ham" | "spam";
};
export type TrafficDay = {
  date: string;
  /** Pages loaded. */
  pageViews: number;
  /** Page loads arriving from another site, or typed in directly.
    Not a count of distinct people.
     */
  visits: number;
};
export type TrafficReport = {
  /** Today's date in the platform's time zone when the report was
    read. Weeks, the current partial week and how far behind
    `through` is are counted from this date, never from the
    client's own clock.
     */
  today: string;
  /** `notConnected`: nothing is collected for this site. `waiting`:
    collection has started and nothing has arrived yet. `current`:
    collected through a day no more than two days ago. `stale`:
    collection has fallen behind. `disconnected`: collection has
    stopped and the days already collected remain.
     */
  state: "notConnected" | "waiting" | "current" | "stale" | "disconnected";
  /** The counts are estimates scaled up from a sample. Present when
    `days` has a row.
     */
  sampled?: boolean;
  /** How the newest row was counted: `browser` by a script in the
    visitor's browser, which some visitors block and search
    engines do not run; `server` from every request the host saw;
    `pushed` by the site itself. Present when `days` has a row.
     */
  method?: "browser" | "server" | "pushed";
  /** The earliest day ever collected for the site. */
  since?: string;
  /** The latest day collected. Absent while nothing has been.
   */
  through?: string;
  /** The collected days in the range, oldest first. */
  days: TrafficDay[];
};
export type RendererFinding = {
  /** `unimplemented`: a type reaches the renderer and it has no case
    for it. `contract`: the name matches and the field contract
    does not. `region`: the type is drawn and one of its regions is
    not. `slot`: a page slot is not drawn. `expanded`: the
    renderer implements something delivery consumes before the
    renderer boundary.
     */
  fault: "unimplemented" | "contract" | "region" | "slot" | "expanded";
  type?: string;
  region?: string;
  slot?: string;
  /** The finding as a sentence, for a deployment log. */
  detail: string;
};
export type RendererCoverage = {
  presentation: string;
  release: string;
  /** The manifest's own contract digest. Two releases with the same
    digest implement the same contract, whatever their tags say.
     */
  digest: string;
  covered: boolean;
  findings: RendererFinding[];
  /** Types the manifest listed with no contract digest, so only
    their names were compared. Not findings.
     */
  undigested?: string[];
};
export type RendererTerminal = {
  /** The type's permanent name. */
  type: string;
  /** The field contract this renderer was built against. Omit it
    only until the build records one: an omitted digest is not a
    mismatch, and the coverage report lists what it could not
    compare.
     */
  digest?: string;
  /** The named regions of this type the renderer draws. A region it
    does not draw holds children that are in the tree and nowhere
    on the page.
     */
  regions?: string[];
};
export type RendererManifest = {
  /** The value a Website selects this renderer on. */
  presentation?: string;
  /** The exact build this was generated from. Opaque to the
    platform and reported back verbatim.
     */
  release?: string;
  terminals: RendererTerminal[];
  /** The page slots this renderer draws. A slot it does not draw is
    content composed and never shown.
     */
  slots: string[];
};
export type EventType = {
  type: string;
  level: "website" | "tenant";
  title: string;
  description?: string;
  dimensions: string[];
};
export type EventTypes = {
  eventTypes: EventType[];
};
export type WebhookDestination = {
  /** An HTTPS endpoint with no embedded credentials. */
  url: string;
};
export type WebhookConditions = {
  [key: string]: string[];
};
export type Webhook = {
  id: string;
  /** A type returned by the event catalogue. */
  type: string;
  destination: WebhookDestination;
  conditions: WebhookConditions;
  enabled: boolean;
  disabledReason?: string;
  createdAt: string;
  updatedAt: string;
};
export type Webhooks = {
  webhooks: Webhook[];
};
export type CreatedWebhook = Webhook & {
  /** The signing secret, returned only by creation and rotation. */
  secret: string;
};
export type CreateWebhook = {
  /** The idempotency key for this creation. */
  id: string;
  type: string;
  destination: WebhookDestination;
  conditions?: WebhookConditions;
};
export type UpdateWebhook = {
  destination?: WebhookDestination;
  conditions?: WebhookConditions;
  enabled?: boolean;
};
export type RotatedWebhookSecret = {
  /** The new signing secret, returned only in this response. */
  secret: string;
  previousExpiresAt: string;
};
export type WebhookDelivery = {
  id: string;
  eventId: string;
  type: string;
  test: boolean;
  status: "pending" | "delivered" | "failed" | "abandoned";
  attempt: number;
  code?: number;
  durationMs?: number;
  error?: string;
  occurredAt: string;
  settledAt?: string;
};
export type WebhookDeliveries = {
  deliveries: WebhookDelivery[];
};
export const {
  useGetManifestQuery,
  useLazyGetManifestQuery,
  useListTenantsQuery,
  useLazyListTenantsQuery,
  useListComponentsQuery,
  useLazyListComponentsQuery,
  useCreateComponentMutation,
  useGetAuthoringComponentQuery,
  useLazyGetAuthoringComponentQuery,
  useGetAuthoringComponentRevisionQuery,
  useLazyGetAuthoringComponentRevisionQuery,
  useListUsagesQuery,
  useLazyListUsagesQuery,
  useApplyCommandMutation,
  useListTenantDomainsQuery,
  useLazyListTenantDomainsQuery,
  useGrantUploadMutation,
  useFinaliseUploadMutation,
  useListPublicationsQuery,
  useLazyListPublicationsQuery,
  usePublishWebsiteMutation,
  useGetPublicationQuery,
  useLazyGetPublicationQuery,
  usePromotePublicationMutation,
  useGetPublicationChangesQuery,
  useLazyGetPublicationChangesQuery,
  useGetPublicationOperationQuery,
  useLazyGetPublicationOperationQuery,
  useListSubmissionsQuery,
  useLazyListSubmissionsQuery,
  useGetSubmissionQuery,
  useLazyGetSubmissionQuery,
  useJudgeSubmissionMutation,
  useResendSubmissionMutation,
  useGetWebsiteTrafficQuery,
  useLazyGetWebsiteTrafficQuery,
  useCheckRendererCoverageMutation,
  useGetRendererContractQuery,
  useLazyGetRendererContractQuery,
  useListEventTypesQuery,
  useLazyListEventTypesQuery,
  useListWebhooksQuery,
  useLazyListWebhooksQuery,
  useCreateWebhookMutation,
  useGetWebhookQuery,
  useLazyGetWebhookQuery,
  useUpdateWebhookMutation,
  useDeleteWebhookMutation,
  useRotateWebhookSecretMutation,
  useTestWebhookMutation,
  useListWebhookDeliveriesQuery,
  useLazyListWebhookDeliveriesQuery,
  useRedeliverWebhookMutation,
  useGetAuthoringOpenApiyamlQuery,
  useLazyGetAuthoringOpenApiyamlQuery,
} = injectedRtkApi;
